/*
 * Bella Mia (S3 Lumi parity, 12.10.2026): the toggle switches of the cart drawer, like Lumi's drawer.
 * - Toggle cards (snippets/bm-cart-upsell.liquid): ON adds the card's variant with /cart/add.js, OFF removes the
 *   card's lines with /cart/change.js (quantity 0, by line key).
 * - The insurance toggle "ביטוח משלוח & עדיפות אריזה" (snippets/bm-cart-summary.liquid): the same, and OFF also
 *   stores the opt-out bmInsOptOut in sessionStorage, so the gift guard (snippets/bm-cart-gift-guard.liquid)
 *   does not add it again on the next add of the eye tightener this session (Lumi: lumiInsOptOut). ON clears it.
 * - A variant dropdown on a card (only when the product has variants) updates the price; while the card is ON it
 *   swaps the line to the chosen variant.
 * Every request carries the cart-items section ids and resolves Horizon's CartLinesUpdateEvent, so the drawer,
 * /cart and the header count morph from the response.
 * 14.10 SWAP+B RB-CART (cart-header-spec 1.5): two or more toggle cards sit in Lumi's slider (one card per view,
 * 45px apart, scroll snap): drag with the mouse (touch scrolls natively), the dots follow the card in view, no
 * arrows, no autoplay. A drag never toggles the switch under the pointer.
 * 14.10 CRO CART (cro-spec item 17): the upgrade card (data-kind="upgrade", snippets/bm-cart-upsell.liquid) swaps the one
 * single tube for the pair: ONE /cart/add.js with the pair (carrying the single line's selling plan, if any; the plan
 * guard still decides) and the PDP bundle's gifts (_bm_gift "pair": one Velvet unit only when no Velvet unit is in the
 * cart, one mystery gift, the map gift only while the gate is on), then the single line out with /cart/change.js.
 * The pair goes in first, so the cart never loses its tightener (insurance and express line stay). The gift guard and
 * the timer take it from there; the drawer morphs from the last response.
 * 15.10 ROUND D RD-CART (round-d-spec D4): a card rendered in a cart with a subscription (data-bm-plan, a tightener line
 * with plan 15406825670) adds its product WITH that plan, in its own /cart/add.js (snippets/bm-plan-guard.liquid
 * re-checks the live cart; the gift guard switches it back to one-time when the subscription goes). The same for a
 * variant change while the card is ON. The upgrade card with a planned single adds the gift Velvet (_bm_gift, never a
 * plan) unless a _bm_gift Velvet is already in the cart: her own Velvet rides the subscription, and D2 frees only a
 * one-time Velvet.
 * 15.10 FOLLOW-UP FUP F4 (boss decision 15.10, replaces the line above): the upgrade card adds the gift Velvet ONLY when
 * the cart has no Velvet unit at all (marked or hers), with or without the plan. Her own Velvet unit becomes the pair's
 * free Velvet: D2 "מכחול Velvet במתנה" frees a one-time Velvet unit (checked live on cart.js 15.10), so when all her
 * Velvet unit lines ride the plan, one unit leaves it (a line of one unit: selling_plan null; a line of 2+ units: one
 * unit less on the riding line + one unit added one-time). Order: the pair (with the single's plan) and the missing
 * gifts, then her Velvet, then the single out (that response renders the drawer). The gift guard
 * (snippets/bm-cart-gift-guard.liquid) is held meanwhile (window.__bmGiftGuardHold) and checks the final cart right
 * after; it keeps that Velvet one-time while the pair is in. The single goes out by a fresh /cart.js lookup (same variant,
 * same plan), because a cart line's key changes when its discounts change (the pair moves the BXGY off a 39 ₪ Velvet).
 * 15.10 ROUND E CART (docs/sagiv-15.10/subscription-renewal-spec.md R1 + R2, replaces the two F4 lines above):
 * - R1: with a planned single the pair goes in with ITS OWN plan, data-pair-plan (15423799494: 199 ₪ then 298 ₪ every
 *   28 days; snippets/bm-cart-upsell.liquid falls back to 15406825670 when the pair has no such allocation). The plan
 *   guard maps either id to the pair's plan anyway.
 * - R2: the upgrade adds the pair and, while the gate is on, the map gift (never a plan) in one /cart/add.js. The gift
 *   Velvet product and the mystery gift come from the gift guard right after the hold, each in its own request, with
 *   plan 15406825670 when the pair has a plan. Her own paid Velvet is replaced by the gift Velvet there too (she never
 *   pays for what the pair includes). The regular Velvet is never added as the pair's gift any more.
 * - F10 (Sagiv 15.10, F6 decision row): a variant change on a card also updates "הנחה X%" (data-off of the option).
 */
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
const OPT_OUT = 'bmInsOptOut';
const JSON_HEADERS = { 'Content-Type': 'application/json', Accept: 'application/json' };

function sectionIds() {
  return Array.from(new Set(
    Array.from(document.querySelectorAll('cart-items-component'))
      .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : ''))
      .filter(Boolean)
  ));
}

/** @param {boolean} optOut */
function setOptOut(optOut) {
  try {
    if (optOut) sessionStorage.setItem(OPT_OUT, '1');
    else sessionStorage.removeItem(OPT_OUT);
  } catch (error) {
    // storage blocked: the toggle still works for this cart
  }
}

/**
 * @param {string} url
 * @param {Record<string, any>} body
 */
async function post(url, body) {
  const response = await fetch(url, {
    method: 'POST',
    headers: JSON_HEADERS,
    credentials: 'same-origin',
    body: JSON.stringify(body),
  });
  const data = await response.json();
  if (!response.ok || (data && data.status)) {
    throw new Error((data && (data.description || data.message)) || 'cart request failed');
  }
  return data;
}

/**
 * Removes `keys` (quantity 0) and/or adds `variantId`, then resolves one CartLinesUpdateEvent with the
 * rendered sections of the last request.
 * @param {HTMLElement} card
 * @param {{ add?: number, remove?: string[] }} change
 */
async function apply(card, change) {
  const ids = sectionIds();
  const extra = { sections: ids.join(','), sections_url: window.location.pathname };
  const removes = change.remove || [];
  const adding = Boolean(change.add);

  const deferred = CartLinesUpdateEvent.createPromise();
  card.dispatchEvent(
    new CartLinesUpdateEvent({
      action: adding ? 'add' : 'remove',
      context: 'cart',
      lines: adding
        ? [{ merchandiseId: String(change.add), quantity: 1 }]
        : removes.map((key) => ({ id: key, quantity: 0 })),
      promise: deferred.promise,
    })
  );

  try {
    let data = null;
    for (let i = 0; i < removes.length; i += 1) {
      const last = i === removes.length - 1 && !adding;
      data = await post(`${ROOT}cart/change.js`, { id: removes[i], quantity: 0, ...(last ? extra : {}) });
    }
    let cart = data;
    if (adding) {
      // 15.10 RD-CART (D4): in a cart with a subscription the rider joins it (the plan guard re-checks the live cart)
      const plan = Number(card.dataset.bmPlan) || 0;
      const item = plan ? { id: change.add, quantity: 1, selling_plan: plan } : { id: change.add, quantity: 1 };
      data = await post(`${ROOT}cart/add.js`, { items: [item], ...extra });
      const response = await fetch(`${ROOT}cart.js`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
      cart = await response.json();
    }
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        itemCount: cart.item_count,
        sections: (data && data.sections) || undefined,
        didError: false,
        source: 'bm-cart-toggle',
      },
    });
    return true;
  } catch (error) {
    deferred.reject(error);
    return false;
  }
}

/**
 * 14.10 CRO CART item 17: single tube -> pair, with the gifts of the PDP bundle.
 * @param {HTMLElement} card
 * @param {HTMLElement} button
 */
async function upgrade(card, button) {
  if (card.dataset.busy === 'true' || card.classList.contains('is-on')) return;
  const pairId = Number(card.dataset.variantId) || 0;
  const fromKey = card.dataset.fromKey || '';
  if (!pairId || !fromKey) return;
  card.dataset.busy = 'true';
  card.classList.add('is-on');
  button.setAttribute('aria-checked', 'true');

  const ids = sectionIds();
  const extra = { sections: ids.join(','), sections_url: window.location.pathname };
  const deferred = CartLinesUpdateEvent.createPromise();
  card.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'cart',
      lines: [{ merchandiseId: String(pairId), quantity: 1 }],
      promise: deferred.promise,
    })
  );

  // 15.10 FOLLOW-UP F4: the gift guard waits until the whole swap is done, then checks the final cart
  const hold = /** @type {any} */ (window).__bmGiftGuardHold;
  if (typeof hold === 'function') hold(10000);

  try {
    const response = await fetch(`${ROOT}cart.js`, { headers: { Accept: 'application/json' }, credentials: 'same-origin', cache: 'no-store' });
    const cart = await response.json();
    const items = Array.isArray(cart.items) ? cart.items : [];
    const isGift = (/** @type {any} */ item) => Boolean(item.properties && item.properties._bm_gift);
    const riding = (/** @type {any} */ item) => Boolean(item.selling_plan_allocation && item.selling_plan_allocation.selling_plan);
    const fromVariant = Number(String(fromKey).split(':')[0]) || 0;
    const from =
      items.find((/** @type {any} */ item) => item.key === fromKey) ||
      items.find((/** @type {any} */ item) => item.variant_id === fromVariant && !isGift(item) && item.quantity > 0);
    if (!from) throw new Error('the single tube is no longer in the cart');
    const plan =
      from.selling_plan_allocation && from.selling_plan_allocation.selling_plan ? from.selling_plan_allocation.selling_plan.id : null;
    // 15.10 ROUND E CART (R1): the pair takes its own plan (the plan guard maps either id to it as well)
    const pairPlan = plan ? Number(card.dataset.pairPlan) || Number(plan) : null;
    const gift = { _bm_gift: 'pair' };
    /** @type {Array<Record<string, any>>} */
    const add = [pairPlan ? { id: pairId, quantity: 1, selling_plan: pairPlan } : { id: pairId, quantity: 1 }];
    const mapId = Number(card.dataset.mapId) || 0;
    const has = (/** @type {number} */ id, /** @type {string} */ handle) =>
      items.some((/** @type {any} */ item) => item.variant_id === id || (handle && item.handle === handle));
    // R2: only the map gift goes with the pair (never a plan); the gift guard adds the gift Velvet and the mystery gift
    if (card.dataset.gate === 'on' && mapId && !has(mapId, 'under-eye-map-gift')) add.push({ id: mapId, quantity: 1, properties: gift });

    // 19.10 PACK SWAP: this card swaps the single for the pair itself (below), so the plan guard's swap stays out
    /** @type {any} */ (window).__bmNoSwap = true;
    try {
      await post(`${ROOT}cart/add.js`, { items: add });
    } finally {
      /** @type {any} */ (window).__bmNoSwap = false;
    }

    // 15.10 FOLLOW-UP F4: line keys change when their discounts change (checked live 15.10: with a one-time single and a
    // 39 ₪ Velvet, the pair add moves the BXGY off and gives the single and the Velvet new keys), so every later step
    // finds its line in a fresh /cart.js read
    const fresh = async () => {
      const res = await fetch(`${ROOT}cart.js`, { headers: { Accept: 'application/json' }, credentials: 'same-origin', cache: 'no-store' });
      const json = await res.json();
      return Array.isArray(json.items) ? json.items : [];
    };

    // the single tube by its variant and plan (its key may have changed), the old key first
    const fromPlan = plan ? Number(plan) : null;
    const now = await fresh();
    const singles = now.filter(
      (/** @type {any} */ item) =>
        item.variant_id === from.variant_id &&
        !isGift(item) &&
        (riding(item) ? Number(item.selling_plan_allocation.selling_plan.id) : null) === fromPlan
    );
    const target = singles.find((/** @type {any} */ item) => item.key === fromKey) || singles[0];
    if (!target) throw new Error('the single tube is no longer in the cart');
    const data = await post(`${ROOT}cart/change.js`, { id: target.key, quantity: 0, ...extra });
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(data),
      detail: {
        items: data.items,
        itemCount: data.item_count,
        sections: data.sections || undefined,
        didError: false,
        source: 'bm-cart-toggle',
      },
    });
  } catch (error) {
    deferred.reject(error);
    card.classList.remove('is-on');
    button.setAttribute('aria-checked', 'false');
  }
  // 15.10 FOLLOW-UP F4: the swap is done (or failed): the gift guard checks the final cart now
  if (typeof hold === 'function') hold(0);
  delete card.dataset.busy;
}

/**
 * @param {HTMLElement} card
 * @param {HTMLElement} button
 */
async function toggle(card, button) {
  if (card.dataset.kind === 'upgrade') {
    await upgrade(card, button);
    return;
  }
  if (card.dataset.busy === 'true') return;
  const wasOn = card.classList.contains('is-on');
  const variantId = Number(card.dataset.variantId);
  const keys = (card.dataset.keys || '').split(',').filter(Boolean);
  if (!wasOn && !variantId) return;
  if (wasOn && !keys.length) return;

  card.dataset.busy = 'true';
  card.classList.toggle('is-on', !wasOn);
  button.setAttribute('aria-checked', String(!wasOn));
  if (card.dataset.kind === 'ins') setOptOut(wasOn);

  const ok = await apply(card, wasOn ? { remove: keys } : { add: variantId });
  if (!ok) {
    card.classList.toggle('is-on', wasOn);
    button.setAttribute('aria-checked', String(wasOn));
    if (card.dataset.kind === 'ins') setOptOut(!wasOn);
  }
  delete card.dataset.busy;
}

/** @param {HTMLSelectElement} select */
async function changeVariant(select) {
  const card = select.closest('[data-bm-toggle-card]');
  if (!(card instanceof HTMLElement) || card.dataset.busy === 'true') return;
  const option = select.selectedOptions[0];
  if (!option) return;
  card.dataset.variantId = option.value;
  const price = card.querySelector('[data-bm-price]');
  const compare = card.querySelector('[data-bm-compare]');
  const off = card.querySelector('[data-bm-off]');
  if (price) price.textContent = option.dataset.price || '';
  if (compare) {
    compare.textContent = option.dataset.compare || '';
    const struck = compare.closest('s');
    if (struck) struck.hidden = !option.dataset.compare;
  }
  // F10: "הנחה X%" follows the chosen option (hidden without a struck price)
  if (off instanceof HTMLElement) {
    off.textContent = option.dataset.off || '';
    off.hidden = !option.dataset.off;
  }
  if (!card.classList.contains('is-on')) return;
  const keys = (card.dataset.keys || '').split(',').filter(Boolean);
  card.dataset.busy = 'true';
  await apply(card, { remove: keys, add: Number(option.value) });
  delete card.dataset.busy;
}

if (!window.__bmCartTogglesBound) {
  window.__bmCartTogglesBound = true;
  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const button = target.closest('[data-bm-toggle]');
    if (!(button instanceof HTMLElement)) return;
    const card = button.closest('[data-bm-toggle-card]');
    if (!(card instanceof HTMLElement)) return;
    event.preventDefault();
    toggle(card, button);
  });
  document.addEventListener('change', (event) => {
    const target = event.target;
    if (target instanceof HTMLSelectElement && target.matches('[data-bm-toggle-select]')) changeVariant(target);
  });
}

/* ---------- Lumi's upsell slider (14.10 SWAP+B RB-CART) ---------- */

/** @param {HTMLElement} track */
function sliderIndex(track) {
  const card = track.firstElementChild;
  if (!(card instanceof HTMLElement)) return 0;
  const step = card.getBoundingClientRect().width + (parseFloat(getComputedStyle(track).columnGap) || 0);
  if (!step) return 0;
  return Math.max(0, Math.min(track.children.length - 1, Math.round(Math.abs(track.scrollLeft) / step)));
}

/** @param {HTMLElement} track */
function syncDots(track) {
  const slider = track.closest('[data-bm-uslider]');
  if (!slider) return;
  const active = sliderIndex(track);
  slider.querySelectorAll('.bm-uslider__dot').forEach((dot, index) => dot.classList.toggle('is-active', index === active));
}

function syncAllSliders() {
  document.querySelectorAll('[data-bm-uslider-track]').forEach((track) => {
    if (track instanceof HTMLElement) syncDots(track);
  });
}

if (!window.__bmCartSliderBound) {
  window.__bmCartSliderBound = true;

  // the dots follow the card in view (scroll does not bubble: listen in the capture phase)
  document.addEventListener(
    'scroll',
    (event) => {
      const track = event.target;
      if (track instanceof HTMLElement && track.matches('[data-bm-uslider-track]')) syncDots(track);
    },
    true
  );

  // mouse drag (touch and pens scroll natively with snap)
  /** @type {{ track: HTMLElement, x: number, left: number, moved: boolean, id: number } | null} */
  let drag = null;
  let suppressClick = false;

  document.addEventListener('pointerdown', (event) => {
    if (event.pointerType !== 'mouse' || event.button !== 0) return;
    const target = event.target instanceof Element ? event.target : null;
    const track = target ? target.closest('[data-bm-uslider-track]') : null;
    if (!(track instanceof HTMLElement) || target.closest('select')) return;
    drag = { track, x: event.clientX, left: track.scrollLeft, moved: false, id: event.pointerId };
  });

  document.addEventListener('pointermove', (event) => {
    if (!drag || event.pointerId !== drag.id) return;
    const dx = event.clientX - drag.x;
    if (!drag.moved && Math.abs(dx) < 6) return;
    if (!drag.moved) {
      drag.moved = true;
      drag.track.classList.add('is-dragging');
    }
    drag.track.scrollLeft = drag.left - dx;
    event.preventDefault();
  });

  const endDrag = () => {
    if (!drag) return;
    const { track, moved } = drag;
    drag = null;
    if (!moved) return;
    suppressClick = true;
    setTimeout(() => {
      suppressClick = false;
    }, 0);
    const card = track.children[sliderIndex(track)];
    track.classList.remove('is-dragging');
    if (card instanceof HTMLElement) {
      const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      card.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'nearest', inline: 'start' });
    }
    syncDots(track);
  };
  document.addEventListener('pointerup', endDrag);
  document.addEventListener('pointercancel', endDrag);

  // a drag that ends on a switch is not a click
  document.addEventListener(
    'click',
    (event) => {
      if (!suppressClick) return;
      const target = event.target instanceof Element ? event.target : null;
      if (target && target.closest('[data-bm-uslider-track]')) {
        event.preventDefault();
        event.stopPropagation();
      }
    },
    true
  );

  // after every cart change the drawer is morphed: put the dots back on the card in view
  const eventName = StandardEvents && StandardEvents.cartLinesUpdate;
  if (eventName) {
    document.addEventListener(eventName, (event) => {
      const promise = /** @type {any} */ (event).promise;
      const later = () => setTimeout(syncAllSliders, 80);
      if (promise && typeof promise.then === 'function') promise.then(later, later);
      else later();
    });
  }
}
